import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/Tooltip';
import { cn } from '../../lib/utils';

// ----------------------------------------------------------------------

const _createOptions = (min: number, max: number) =>
  Array.from({ length: max - min + 1 }, (_, i) => i + min);

type Props = {
  name: string;
  readOnly?: boolean;
  min?: number;
  max?: number;
  /** Map of value -> tooltip text (e.g. { 1: 'Not likely at all' }) */
  tooltip?: Record<number, string>;
  size?: 'small';
  /** Submit the surrounding form when a value is selected */
  shouldSubmit?: boolean;
  /**
   * When set, each option renders as an `<a>` link (opened in a new tab) to
   * `href` with `searchParams` plus the option appended as `score=<value>`,
   * mirroring the NPS survey email links.
   */
  href?: string;
  /** Query params appended to `href` (`score` is added automatically) */
  searchParams?: Record<string, string>;
};

const RHFNumberLine = ({
  name,
  readOnly,
  min = 1,
  max = 10,
  tooltip,
  size,
  shouldSubmit,
  href,
  searchParams
}: Props) => {
  const { control } = useFormContext();
  const scalePickerOptions = useMemo(() => _createOptions(min, max), [min, max]);

  const buildHref = (option: number) => {
    const params = new URLSearchParams({ ...searchParams, score: String(option) });
    return `${href}?${params.toString()}`;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          <TooltipProvider>
            <div role="group" aria-label="Pick a rating" className="flex w-full">
              {scalePickerOptions.map((option, index) => {
                const selected = field.value === option;
                const tooltipText = tooltip ? tooltip[option] : '';

                const itemClassName = cn(
                  'inline-flex min-w-0 flex-1 items-center justify-center border border-input bg-background text-sm font-medium shadow-sm transition-colors',
                  'hover:bg-accent hover:text-accent-foreground',
                  'focus-visible:z-10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  index > 0 && '-ml-px',
                  index === 0 && 'rounded-l-md',
                  index === scalePickerOptions.length - 1 && 'rounded-r-md',
                  size === 'small' ? 'h-8 max-w-[36px]' : 'h-9',
                  selected &&
                    'z-10 border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground'
                );

                const item = href ? (
                  <a
                    key={`nl-${name}-${option}`}
                    href={buildHref(option)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      itemClassName,
                      'no-underline',
                      readOnly && 'pointer-events-none opacity-50'
                    )}
                    onClick={(event) => {
                      field.onChange(option);
                      if (shouldSubmit) {
                        // Anchors can't be submit buttons; submit the enclosing form manually.
                        event.currentTarget.closest('form')?.requestSubmit();
                      }
                    }}
                  >
                    {option}
                  </a>
                ) : (
                  <button
                    key={`nl-${name}-${option}`}
                    type={shouldSubmit ? 'submit' : 'button'}
                    aria-pressed={selected}
                    disabled={readOnly}
                    onClick={() => {
                      if (!readOnly) {
                        if (selected) {
                          field.onChange(null);
                        } else {
                          field.onChange(option);
                        }
                      }
                    }}
                    className={cn(
                      itemClassName,
                      'disabled:pointer-events-none disabled:opacity-50'
                    )}
                  >
                    {option}
                  </button>
                );

                if (!tooltipText) {
                  return item;
                }

                return (
                  <Tooltip key={`nl-${name}-${option}`}>
                    <TooltipTrigger asChild>{item}</TooltipTrigger>
                    <TooltipContent side="top">{tooltipText}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>

          {error && error.message && <p className="text-xs text-destructive">{error.message}</p>}
        </>
      )}
    />
  );
};

export default RHFNumberLine;
